import React, { useCallback, useEffect } from 'react';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { withLocalePrefix } from '../../../shared/utils/translation';
import TextField from '@grnhse/seedling/lib/birch/components/forms/fields/text_field';
import {
  FormProvider,
  useForm,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import SingleSelectField from '@grnhse/seedling/lib/birch/components/forms/fields/single_select_field';
import { NestedFormSubmitButton } from './shared';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { ModalBodyWrapper } from './insert_web_link_modal';

const t = withLocalePrefix(
  'generic_booking.tinymce_editor.insert_scheduling_link_modal'
);

type InsertSchedulingLinkModalProps = {
  showModal: boolean;
  handleClose: () => void;
  insertLink: (href: string, text?: string) => void;
  schedulingLinksOptions: Array<Item>;
  initialData?: { href: string; text: string } | null;
  isEditing?: boolean;
};

type SchedulingLinkForm = {
  displayText: string;
  schedulingLink: Item;
};

const InsertSchedulingLinkModal = ({
  showModal,
  handleClose,
  insertLink,
  schedulingLinksOptions,
  initialData,
  isEditing,
}: InsertSchedulingLinkModalProps) => {
  const fetchDefault = useCallback(() => {
    const defaultSchedulingLink = schedulingLinksOptions[0] || null;

    if (!initialData) {
      return { schedulingLink: defaultSchedulingLink };
    }

    const schedulingLink = isEditing
      ? schedulingLinksOptions.find(
          (link) => link.value === initialData.href
        ) || defaultSchedulingLink
      : defaultSchedulingLink;

    const displayText =
      initialData.text && (!isEditing || initialData.text !== initialData.href)
        ? initialData.text
        : '';

    return { schedulingLink, displayText };
  }, [schedulingLinksOptions, initialData, isEditing]);

  const form = useForm<SchedulingLinkForm>({ mode: 'onBlur' });

  useEffect(() => {
    form.reset(fetchDefault());
  }, [fetchDefault, form]);

  const resetAndCloseModal = () => {
    form.reset();
    handleClose();
  };

  const saveChanges = async (data: FieldValues) => {
    insertLink(data.schedulingLink.value, data.displayText);
    resetAndCloseModal();
  };

  return (
    <FormProvider {...form}>
      <DataEntryModal
        size={'medium'}
        isOpen={showModal}
        onDismiss={handleClose}
        onSubmit={() => null}
        ariaLabel={'insert-scheduling-link-modal'}
      >
        <DataEntryModal.Header onClick={resetAndCloseModal}>
          {isEditing ? t('title.edit') : t('title.insert')}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <ModalBodyWrapper data-provides={'insert-web-link-modal'}>
            <Stack spacing={3}>
              <Body>{t('description')}</Body>
              <SingleSelectField
                items={schedulingLinksOptions}
                label={t('scheduling_link')}
                name={'schedulingLink'}
                disabled={schedulingLinksOptions.length === 0}
              />
              <TextField name={'displayText'} label={t('text_to_display')} />
            </Stack>
          </ModalBodyWrapper>
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Grid
            container
            spacing={2}
            alignItems="flex-start"
            direction="row"
            justifyContent="flex-end"
            flexWrap="wrap-reverse"
            sx={{
              padding: '8px',
            }}
          >
            <Grid>
              <SecondaryButton onClick={resetAndCloseModal}>
                {t('cancel')}
              </SecondaryButton>
            </Grid>
            <Grid>
              {/* FYI: this cannot be a submit button "inside" of the other modal*/}
              {/* Reference app/webpack/javascripts/billing/modals/review_all_information/modal_footer.tsx */}
              <NestedFormSubmitButton
                buttonText={isEditing ? t('update') : t('add')}
                handleSubmit={saveChanges}
              />
            </Grid>
          </Grid>
        </DataEntryModal.Footer>
      </DataEntryModal>
    </FormProvider>
  );
};

export default InsertSchedulingLinkModal;
